import React, { useMemo } from 'react';

import { VendorPicker } from 'components/procurement/components/purchaseOrdersV2/formComponents/VendorPicker';
import { formatPurchaseOrderDateTime } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatNumber,
    normalizeOptionalText,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Separator } from 'shadcn/components/ui/Separator';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';
const readOnlyValueClassName = 'text-left text-base font-medium text-muted-foreground/70';

export function PackagingPurchaseOrderPrimaryInfoCard({
    draft,
    isEditing = false,
    onDraftChange,
    primaryErrors = [],
    primaryFieldErrors = {},
    purchaseOrder = null,
    showValidation = false,
    totalCases,
    totalPallets,
    totalUnits,
}) {
    const { facilityName, referenceNumber, scheduledDeliveryDate, vendorName } = purchaseOrder ?? {};

    const infoSections = useMemo(
        () => [
            { label: 'Vendor Name', value: normalizeOptionalText(vendorName) },
            { label: 'Facility', value: normalizeOptionalText(facilityName) },
            {
                label: 'Scheduled Delivery',
                value: formatPurchaseOrderDateTime(scheduledDeliveryDate, FALLBACK_TEXT),
            },
            { label: 'Reference Number', value: normalizeOptionalText(referenceNumber) },
            {
                label: 'Total Pallets',
                value: formatNumber(totalPallets, { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
            },
            {
                label: 'Total Cases',
                value: formatNumber(totalCases, { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
            },
            {
                label: 'Total Units',
                value: formatNumber(totalUnits, { maximumFractionDigits: 0, minimumFractionDigits: 0 }),
            },
        ],
        [facilityName, referenceNumber, scheduledDeliveryDate, totalPallets, totalCases, totalUnits, vendorName],
    );

    if (!isEditing) {
        return (
            <div className="rounded-md border bg-card p-6 shadow-sm">
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-left text-lg font-semibold tracking-tight text-foreground">
                            Packaging Purchase Order Information
                        </h2>
                    </div>
                    <dl className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
                        {infoSections.map(({ label, value }) => (
                            <div key={label} className="flex flex-col gap-1">
                                <dt className={labelClassName}>{label}</dt>
                                <dd className="text-left text-base font-medium text-foreground">{value}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        );
    }

    const showErrors = showValidation === true;
    const getFieldError = (field) => (showErrors ? (primaryFieldErrors?.[field] ?? '') : '');
    const vendorError = getFieldError('vendorId');
    const scheduledDeliveryDateError = getFieldError('scheduledDeliveryDate');
    const scheduledDeliveryTimeError = getFieldError('scheduledDeliveryTime');

    return (
        <div className="rounded-md border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-1">
                    <h2 className="text-left text-lg font-semibold tracking-tight text-foreground">
                        Edit Packaging Purchase Order Information
                    </h2>
                    <p className="text-left text-sm text-muted-foreground">
                        Update the values below. Use the header controls to save or cancel your edits.
                    </p>
                </div>

                {showErrors && primaryErrors.length > 0 ? (
                    <Alert variant="destructive">
                        <AlertTitle className="text-left">Fix the following to save this purchase order</AlertTitle>
                        <AlertDescription className="text-left">
                            <ul className="list-disc pl-5 text-left">
                                {primaryErrors.map((message) => (
                                    <li key={message}>{message}</li>
                                ))}
                            </ul>
                        </AlertDescription>
                    </Alert>
                ) : null}

                <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
                    <div className="flex flex-col gap-2">
                        <span className={labelClassName}>Facility</span>
                        <span className={readOnlyValueClassName}>{normalizeOptionalText(facilityName)}</span>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className={labelClassName}>Total Pallets</span>
                        <span className={readOnlyValueClassName}>
                            {formatNumber(totalPallets, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}
                        </span>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className={labelClassName}>Total Cases</span>
                        <span className={readOnlyValueClassName}>
                            {formatNumber(totalCases, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}
                        </span>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className={labelClassName}>Total Units</span>
                        <span className={readOnlyValueClassName}>
                            {formatNumber(totalUnits, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}
                        </span>
                    </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
                    <div className="flex flex-col gap-2">
                        <VendorPicker
                            value={draft?.vendorId}
                            onValueChange={(id) =>
                                onDraftChange?.((previous) => ({
                                    ...previous,
                                    vendorId: id,
                                }))
                            }
                            label={
                                <>
                                    Vendor <span className="text-destructive">*</span>
                                </>
                            }
                        />
                        {vendorError ? <p className="text-xs text-destructive">{vendorError}</p> : null}
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="scheduledDeliveryDate" className={labelClassName}>
                            Scheduled Delivery Date
                        </Label>
                        <Input
                            id="scheduledDeliveryDate"
                            name="scheduledDeliveryDate"
                            type="date"
                            value={draft?.scheduledDeliveryDate ?? ''}
                            onChange={(event) =>
                                onDraftChange?.((previous) => ({
                                    ...previous,
                                    scheduledDeliveryDate: event.target.value,
                                }))
                            }
                        />
                        {scheduledDeliveryDateError ? (
                            <p className="text-xs text-destructive">{scheduledDeliveryDateError}</p>
                        ) : null}
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="scheduledDeliveryTime" className={labelClassName}>
                            Scheduled Delivery Time
                        </Label>
                        <Input
                            id="scheduledDeliveryTime"
                            name="scheduledDeliveryTime"
                            type="time"
                            value={draft?.scheduledDeliveryTime ?? ''}
                            onChange={(event) =>
                                onDraftChange?.((previous) => ({
                                    ...previous,
                                    scheduledDeliveryTime: event.target.value,
                                }))
                            }
                        />
                        {scheduledDeliveryTimeError ? (
                            <p className="text-xs text-destructive">{scheduledDeliveryTimeError}</p>
                        ) : null}
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="referenceNumber" className={labelClassName}>
                            Reference Number
                        </Label>
                        <Input
                            id="referenceNumber"
                            name="referenceNumber"
                            value={draft?.referenceNumber ?? ''}
                            onChange={(event) =>
                                onDraftChange?.((previous) => ({
                                    ...previous,
                                    referenceNumber: event.target.value,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

PackagingPurchaseOrderPrimaryInfoCard.displayName = 'PackagingPurchaseOrderPrimaryInfoCard';
